import { type AgSunburstSeriesOptions, VERSION } from 'ag-charts-community';
import {
    BASE_FONT_SIZE,
    FILL_GRADIENT_RADIAL_REVERSED_SERIES_DEFAULTS,
    FILL_IMAGE_DEFAULTS,
    FILL_PATTERN_DEFAULTS,
    FONT_SIZE_RATIO,
    LABEL_BOXING_DEFAULTS,
    SERIES_SELECTION_THEME,
    type SeriesModuleDefinition,
} from 'ag-charts-core';
import type { ExtensibleSeriesTheme } from 'ag-charts-types';

import { StandaloneChartModule } from '../../charts/standaloneChartModule';
import { SunburstSeries } from './sunburstSeries';
import { sunburstSeriesOptionsDef } from './sunburstSeriesOptionsDef';

const themeTemplate: ExtensibleSeriesTheme<'sunburst'> = {
    series: {
        fills: {
            $applyCycle: [
                { $size: { $path: ['./data', { $path: '/data' }] } },
                { $palette: 'fills' },
                {
                    $applySwitch: [
                        { $path: ['/type', undefined, { $value: '$1' }] },
                        { $value: '$1' },
                        ['gradient', FILL_GRADIENT_RADIAL_REVERSED_SERIES_DEFAULTS],
                        ['pattern', FILL_PATTERN_DEFAULTS],
                        ['image', FILL_IMAGE_DEFAULTS],
                    ],
                },
            ],
        },
        strokes: {
            $applyCycle: [{ $size: { $path: ['./data', { $path: '/data' }] } }, { $palette: 'strokes' }],
        },
        colorScale: {
            fills: { $map: [{ color: { $value: '$1' } }, { $palette: 'divergingColors' }] },
        },
        strokeWidth: { $isUserOption: ['./strokes/0', 2, 0] },
        label: {
            ...LABEL_BOXING_DEFAULTS,
            enabled: true,
            fontFamily: { $ref: 'fontFamily' },
            fontSize: { $rem: FONT_SIZE_RATIO.LARGE },
            minimumFontSize: { $rem: 9 / BASE_FONT_SIZE },
            fontWeight: { $ref: 'fontWeight' },
            color: { $ref: 'chartBackgroundColor' },
            overflowStrategy: 'ellipsis',
            wrapping: 'never',
            spacing: 2,
        },
        secondaryLabel: {
            ...LABEL_BOXING_DEFAULTS,
            enabled: true,
            fontFamily: { $ref: 'fontFamily' },
            fontSize: { $rem: FONT_SIZE_RATIO.SMALLEST },
            minimumFontSize: { $rem: 7 / BASE_FONT_SIZE },
            fontWeight: { $ref: 'fontWeight' },
            color: { $ref: 'chartBackgroundColor' },
            overflowStrategy: 'ellipsis',
            wrapping: 'never',
        },
        innerLabels: {
            $apply: {
                ...LABEL_BOXING_DEFAULTS,
                fontSize: { $ref: 'fontSize' },
                fontFamily: { $ref: 'fontFamily' },
                fontWeight: { $ref: 'fontWeight' },
                color: { $ref: 'textColor' },
                spacing: 2,
            },
        },
        sectorSpacing: 2,
        padding: 3,
        highlight: {
            enabled: { $circular: { $path: '/highlight/enabled' } },
            unhighlightedItem: {
                fillOpacity: 0.6,
                strokeOpacity: 0.6,
            },
            unhighlightedBranch: {
                fillOpacity: 0.2,
                strokeOpacity: 0.2,
            },
        },
        selection: SERIES_SELECTION_THEME,
    },
    legend: {
        enabled: {
            $and: [{ $path: '../series/0/colorKey' }, { $eq: [{ $path: '../series/0/colorScale/mode' }, 'discrete'] }],
        },
    },
    gradientLegend: {
        enabled: {
            $and: [
                { $path: '../series/0/colorKey' },
                { $not: { $eq: [{ $path: '../series/0/colorScale/mode' }, 'discrete'] } },
            ],
        },
    },
};

export const SunburstSeriesModule: SeriesModuleDefinition<AgSunburstSeriesOptions> = {
    type: 'series',
    name: 'sunburst',
    chartType: 'standalone',
    enterprise: true,
    solo: true,
    version: VERSION,
    dependencies: [StandaloneChartModule],

    options: sunburstSeriesOptionsDef,
    themeTemplate,

    create: (ctx) => new SunburstSeries(ctx),
};
